import { DiscountTypeAPI } from '@business/api/discount_type';
import { DiscountTypeId, DiscountTypeRequest, DiscountTypeResponse } from '@business/entities/tm';
import { handleMessage, requestMessage } from '@business/messages';
import { BaseBloc } from '@core/utils/bloc';
import { R } from '@core/utils/r';
import { Repository } from '@core/utils/repository';
import { showDeleteConfirmation } from '@modules/common';
import { map, switchMap } from 'rxjs/operators';

/** Discount types (Seasonal, Referral...) used by Work Order discounts. Managed in Settings by admins. */
export class DiscountTypeBloc extends BaseBloc {
  private readonly repo = new Repository<DiscountTypeResponse>({
    getItemId: item => item.id,
  });

  readonly items$ = this.repo.items$.pipe(map(items => R.sortBy(items, 'menuOrder')));
  readonly selectItem = this.repo.selectItem;

  onReset() {
    this.repo.reset();
  }

  fetchDiscountTypes = () => {
    return DiscountTypeAPI.fetchDiscountTypes().pipe(
      this.repo.ops.reset(),
      handleMessage({ error: requestMessage('fetch_discount_types_error') }),
    );
  };

  createDiscountType = (data: DiscountTypeRequest) => {
    return DiscountTypeAPI.createDiscountType(data).pipe(
      this.repo.ops.reset(),
      handleMessage({ type: requestMessage('create_discount_type') }),
    );
  };

  updateDiscountType = (id: DiscountTypeId, data: DiscountTypeRequest) => {
    return DiscountTypeAPI.updateDiscountType(id, data).pipe(
      this.repo.ops.reset(),
      handleMessage({ type: requestMessage('update_discount_type') }),
    );
  };

  deleteDiscountType = (id: DiscountTypeId) => {
    return showDeleteConfirmation(
      'Delete Discount Type',
      'Do you really want to remove this discount type? Discounts that already use it keep its name.',
    ).pipe(
      switchMap(() => DiscountTypeAPI.deleteDiscountType(id)),
      this.repo.ops.removeOne(() => id),
      handleMessage({ type: requestMessage('delete_discount_type') }),
    );
  };

  sortDiscountTypes = (ids: DiscountTypeId[]) => {
    return DiscountTypeAPI.sortDiscountTypes(ids).pipe(
      this.repo.ops.reset(),
      handleMessage({ type: requestMessage('update_discount_type') }),
    );
  };
}
